import {
  type IChartApi,
  type ISeriesApi,
  createChart,
  ColorType,
  LineSeries,
} from "lightweight-charts";
import { Panel } from "./panel-base.ts";
import { el } from "../utils/dom.ts";
import { getLatest, getSeries, type LatestEntry } from "../services/api-client.ts";
import { linearTrend } from "../utils/trend-extrapolation.ts";

const RANGES = ["1d", "1w", "1m", "1y"] as const;
type Range = (typeof RANGES)[number];

export interface ChartSeriesDef {
  series: string; // D1 series id
  label: string;
  color: string;
  field: "price" | "buy" | "sell"; // which value to plot
}

export type ValueFormat = "usd" | "vnd-gold" | "vnd-fx" | "percent" | "points";

export interface DerivedPoint {
  ts: number;
  value: number;
}

export interface PriceChartOptions {
  id: string;
  title: string;
  subtitle?: string;
  seriesDefs: ChartSeriesDef[];
  valueFormat: ValueFormat;
  // When true, overlays a dashed 14-day linear-trend projection + volatility band
  // on the first series (reference-only, labelled). Off by default — existing
  // charts are unaffected.
  projection?: boolean;
  // Static caption shown under the chart (e.g. formula explanation). Independent of
  // the projection caption, which only renders when projection is on.
  caption?: string;
  // Optional client-side derivation. When set, the panel fetches each series in
  // `derive.inputs` for the current range, hands the raw points to `compute`, and
  // plots the single returned line under `derive.label` — instead of the normal
  // per-seriesDef plotting. Used for premium % (SJC vs quy đổi) and VN-Index + MA.
  derive?: {
    inputs: string[]; // D1 series ids to fetch
    label: string;
    color: string;
    compute: (raw: Record<string, { ts: number; price: number | null; buy: number | null; sell: number | null }[]>) => DerivedPoint[];
    // Optional second line (e.g. a moving average) computed from the same raw inputs.
    overlay?: { label: string; color: string; compute: (raw: Record<string, { ts: number; price: number | null; buy: number | null; sell: number | null }[]>) => DerivedPoint[] };
  };
}

// Base price chart panel: lightweight-charts with a range switcher + latest-quote header.
export class PriceChartPanel extends Panel {
  private chart: IChartApi | null = null;
  private lines = new Map<string, ISeriesApi<"Line">>();
  private range: Range = "1m";
  private readonly opts: PriceChartOptions;
  private readonly chartHost: HTMLElement;
  private readonly quoteHost: HTMLElement;
  private readonly rangeHost: HTMLElement;
  private readonly captionHost: HTMLElement;
  private projLine: ISeriesApi<"Line"> | null = null;
  private projUpper: ISeriesApi<"Line"> | null = null;
  private projLower: ISeriesApi<"Line"> | null = null;

  constructor(opts: PriceChartOptions) {
    super({ id: opts.id, title: opts.title, subtitle: opts.subtitle });
    this.opts = opts;
    this.quoteHost = el("div", { className: "price-quote" });
    this.rangeHost = el("div", { className: "range-switch" });
    this.chartHost = el("div", { className: "chart-host" });
    this.captionHost = el("div", { className: "chart-caption" });
    this.setContent(this.quoteHost, this.rangeHost, this.chartHost, this.captionHost);
    this.buildRangeButtons();
    if (opts.caption) {
      this.captionHost.append(el("span", { className: "chart-static-caption" }, [opts.caption]));
    }
  }

  private derivedLines: ISeriesApi<"Line">[] = [];

  private buildRangeButtons(): void {
    for (const r of RANGES) {
      const btn = el(
        "button",
        {
          className: r === this.range ? "range-btn active" : "range-btn",
          "data-range": r,
          onclick: () => this.switchRange(r),
        },
        [r],
      );
      this.rangeHost.append(btn);
    }
  }

  private switchRange(r: Range): void {
    this.range = r;
    for (const b of this.rangeHost.querySelectorAll(".range-btn")) {
      b.classList.toggle("active", b.getAttribute("data-range") === r);
    }
    void this.loadSeries();
  }

  async load(): Promise<void> {
    // Derived panels render their own header from the computed value; skip the
    // latest-quote fetch (there is no single D1 series to quote).
    if (this.opts.derive) return this.loadSeries();
    await Promise.all([this.loadQuote(), this.loadSeries()]);
  }

  private async loadQuote(): Promise<void> {
    try {
      const { series } = await getLatest();
      const parts: Node[] = [];
      for (const def of this.opts.seriesDefs) {
        const entry = series.find((s) => s.series === def.series);
        if (!entry) continue;
        parts.push(this.renderQuote(def, entry));
      }
      this.quoteHost.replaceChildren(...parts);
    } catch {
      // leave prior quote; series load handles the visible error state
    }
  }

  private renderQuote(def: ChartSeriesDef, entry: LatestEntry): HTMLElement {
    const value = entry.kind === "mid" ? entry.price : def.field === "buy" ? entry.buy : entry.sell;
    const delta = entry.delta24h;
    const cls = delta ? (delta.pct >= 0 ? "up" : "down") : "";
    const deltaStr = delta ? `${delta.pct >= 0 ? "▲" : "▼"} ${Math.abs(delta.pct).toFixed(2)}%` : "";
    return el("div", { className: "quote-item" }, [
      el("span", { className: "quote-label" }, [def.label]),
      el("span", { className: "quote-value" }, [this.fmt(value)]),
      el("span", { className: `quote-delta ${cls}` }, [deltaStr]),
    ]);
  }

  private fmt(v: number): string {
    switch (this.opts.valueFormat) {
      case "vnd-gold":
        // SJC gold ~150M VND/lượng -> show in triệu.
        return `${(v / 1_000_000).toLocaleString("vi-VN", { maximumFractionDigits: 2 })} tr`;
      case "vnd-fx":
        // FX ~26K VND -> raw đồng.
        return `${v.toLocaleString("vi-VN", { maximumFractionDigits: 0 })} ₫`;
      case "percent":
        return `${v.toLocaleString("vi-VN", { maximumFractionDigits: 1 })}%`;
      case "points":
        // VN-Index ~1850 points.
        return v.toLocaleString("vi-VN", { maximumFractionDigits: 2 });
      default:
        return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }

  private ensureChart(): IChartApi {
    if (this.chart) return this.chart;
    this.chart = createChart(this.chartHost, {
      height: 220,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa4b2",
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: "#1e2733" } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false, timeVisible: this.range === "1d" },
      autoSize: true,
    });
    if (this.opts.derive) {
      // One line for the derived value, plus an optional overlay (e.g. MA).
      this.derivedLines = [
        this.chart.addSeries(LineSeries, { color: this.opts.derive.color, lineWidth: 2 }),
      ];
      if (this.opts.derive.overlay) {
        this.derivedLines.push(
          this.chart.addSeries(LineSeries, {
            color: this.opts.derive.overlay.color,
            lineWidth: 1,
            lineStyle: 2, // dashed MA
            lastValueVisible: false,
          }),
        );
      }
      return this.chart;
    }
    for (const def of this.opts.seriesDefs) {
      const line = this.chart.addSeries(LineSeries, { color: def.color, lineWidth: 2 });
      this.lines.set(def.series + def.field, line);
    }
    if (this.opts.projection) {
      // Dashed projection + faint band, all on the first series' scale.
      this.projLine = this.chart.addSeries(LineSeries, {
        color: "#f5c518",
        lineWidth: 2,
        lineStyle: 2, // dashed
        lastValueVisible: false,
        priceLineVisible: false,
      });
      const bandOpts = {
        color: "rgba(245,197,24,0.35)",
        lineWidth: 1 as const,
        lineStyle: 3 as const, // dotted
        lastValueVisible: false,
        priceLineVisible: false,
      };
      this.projUpper = this.chart.addSeries(LineSeries, bandOpts);
      this.projLower = this.chart.addSeries(LineSeries, bandOpts);
    }
    return this.chart;
  }

  private renderProjection(points: { ts: number; value: number | null }[]): void {
    if (!this.opts.projection || !this.projLine) return;
    const clean = points
      .filter((p) => p.value != null)
      .map((p) => ({ time: Math.floor(p.ts / 1000), value: p.value as number }));
    const trend = linearTrend(clean, 14, 30);
    if (!trend) {
      this.captionHost.replaceChildren();
      return;
    }
    // lightweight-charts wants strictly ascending, unique times.
    const toData = (arr: { time: number; value: number }[]) =>
      arr.filter((d, i, a) => i === 0 || d.time > a[i - 1].time) as never;
    this.projLine.setData(toData(trend.projected));
    this.projUpper?.setData(toData(trend.upper));
    this.projLower?.setData(toData(trend.lower));

    const dir = trend.slopePctPerWeek > 0.1 ? "tăng" : trend.slopePctPerWeek < -0.1 ? "giảm" : "đi ngang";
    const pct = Math.abs(trend.slopePctPerWeek).toFixed(1);
    this.captionHost.replaceChildren(
      el("span", { className: "chart-forecast-label" }, [
        `Xu hướng ${dir} ${pct}%/tuần (30 ngày gần nhất). `,
      ]),
      el(
        "span",
        {
          className: "chart-forecast-note",
          title: "Ngoại suy tuyến tính 14 ngày + dải biến động ±1.5σ. Không phải khuyến nghị đầu tư.",
        },
        ["Tham khảo — ngoại suy xu hướng, không phải khuyến nghị. ⓘ"],
      ),
    );
  }

  // Convert derived points to strictly-ascending unique-time chart data.
  private toChartData(pts: DerivedPoint[]): { time: number; value: number }[] {
    return pts
      .map((p) => ({ time: Math.floor(p.ts / 1000), value: p.value }))
      .filter((d) => Number.isFinite(d.value))
      .sort((a, b) => a.time - b.time)
      .filter((d, i, arr) => i === 0 || d.time > arr[i - 1].time);
  }

  private async loadDerived(): Promise<void> {
    const derive = this.opts.derive;
    if (!derive) return;
    const chart = this.ensureChart();
    try {
      const raw: Record<string, { ts: number; price: number | null; buy: number | null; sell: number | null }[]> = {};
      await Promise.all(
        derive.inputs.map(async (s) => {
          const { points } = await getSeries(s, this.range);
          raw[s] = points;
        }),
      );
      const main = this.toChartData(derive.compute(raw));
      this.derivedLines[0]?.setData(main as never);
      if (derive.overlay && this.derivedLines[1]) {
        this.derivedLines[1].setData(this.toChartData(derive.overlay.compute(raw)) as never);
      }
      // Empty-but-successful is a normal outcome for forward-filled series with no
      // same-day overlap yet (e.g. premium on 1d/1w before SJC history builds). Show
      // an honest "not enough data" note WITHOUT setError() — that would destroy the
      // range switcher and strand the user off the 1y range where data does exist.
      if (main.length === 0) {
        this.quoteHost.replaceChildren(
          el("div", { className: "quote-item" }, [
            el("span", { className: "quote-label" }, [derive.label]),
            el("span", { className: "quote-value muted" }, ["—"]),
          ]),
        );
        this.setDerivedNote("Chưa đủ dữ liệu cùng ngày cho khoảng này. Thử khoảng 1y.");
        return;
      }
      this.clearDerivedNote();
      // Header shows the latest derived value.
      const last = main[main.length - 1];
      this.quoteHost.replaceChildren(
        el("div", { className: "quote-item" }, [
          el("span", { className: "quote-label" }, [derive.label]),
          el("span", { className: "quote-value" }, [this.fmt(last.value)]),
        ]),
      );
      chart.timeScale().fitContent();
    } catch {
      this.setError();
    }
  }

  // A transient "not enough data" note appended after the static caption, cleared
  // once data appears. Kept separate from setError so hosts stay intact.
  private derivedNoteEl: HTMLElement | null = null;
  private setDerivedNote(msg: string): void {
    if (!this.derivedNoteEl) {
      this.derivedNoteEl = el("span", { className: "chart-empty-note" });
      this.captionHost.append(this.derivedNoteEl);
    }
    this.derivedNoteEl.textContent = msg;
  }
  private clearDerivedNote(): void {
    this.derivedNoteEl?.remove();
    this.derivedNoteEl = null;
  }

  private async loadSeries(): Promise<void> {
    if (this.opts.derive) return this.loadDerived();
    const chart = this.ensureChart();
    try {
      await Promise.all(
        this.opts.seriesDefs.map(async (def, idx) => {
          const { points } = await getSeries(def.series, this.range);
          const line = this.lines.get(def.series + def.field);
          if (!line) return;
          const data = points
            .map((p) => ({ time: Math.floor(p.ts / 1000) as never, value: p[def.field] }))
            .filter((d): d is { time: never; value: number } => d.value != null)
            // lightweight-charts requires strictly ascending, unique timestamps
            .filter((d, i, arr) => i === 0 || d.time > arr[i - 1].time);
          line.setData(data);

          // Projection overlays the FIRST series, and only on ranges with enough
          // history (needs ~30 daily points — skip 1d/1w).
          if (idx === 0 && this.opts.projection) {
            if (this.range === "1m" || this.range === "1y") {
              this.renderProjection(points.map((p) => ({ ts: p.ts, value: p[def.field] })));
            } else {
              this.clearProjection();
            }
          }
        }),
      );
      chart.timeScale().fitContent();
    } catch {
      this.setError();
    }
  }

  private clearProjection(): void {
    this.projLine?.setData([]);
    this.projUpper?.setData([]);
    this.projLower?.setData([]);
    this.captionHost.replaceChildren();
  }
}
